import {
  DataNode,
  FormLayout,
  ScoresAndAddonProductsInputProps,
} from '../common';
import { ProgramWizardStepDataProcessor } from '../common/ProgramWizardStepDataProcessor';
import { useDataPart, useStepContext } from '@/app/core/components/step-wizard';
import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers';
import {
  AddonProductLookup,
  ScoresAndAddonProductsInformation,
} from '@ssar-app/domain';
import { Controller, useForm } from 'react-hook-form';
import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  GridItem,
  Tooltip,
} from '@chakra-ui/react';
import { Select, Tree } from '@ssar-app/uikit';
import { ObjectSchema } from 'yup';

export const ScoresAndAddonProducts = (
  props: ScoresAndAddonProductsInputProps,
) => {
  const { stepKey, validator } = props;
  const { lookupsManager } = props.moduleProps;
  const { isStepActive } = useStepContext();
  const [dataPartState] =
    useDataPart<ScoresAndAddonProductsInformation>(stepKey);
  const [scoresAndAddonProductsData, setScoresAndAddonProductsData] = useState<
    Partial<ScoresAndAddonProductsInformation>
  >(dataPartState.data || {});

  const [addonProductsTreeData, setAddonProductsTreeData] = useState<
    DataNode[]
  >([]);

  const scores: string[] =
    scoresAndAddonProductsData.scores?.filter(
      (a) => !lookupsManager.getDisableScoresLookupsCodes().includes(a),
    ) || [];
  const addonProducts: string[] =
    scoresAndAddonProductsData.addonProducts?.filter(
      (a) => !lookupsManager.getDisableAddonProductsLookupsCodes().includes(a),
    ) || [];

  const addonProductsWithGroup: Record<string, AddonProductLookup[]> = {};
  lookupsManager.getAddonProductsLookups().forEach((addonProduct) => {
    addonProductsWithGroup[addonProduct.group] = [
      ...(addonProductsWithGroup[addonProduct.group] || []),
      addonProduct,
    ];
  });

  const validatorContext = {
    context: {
      getScoresLookups: () => {
        return lookupsManager.getScoresLookups();
      },
    },
  };
  const formMethods = useForm<ScoresAndAddonProductsInformation>({
    defaultValues: { scores: scores, addonProducts: addonProducts },
    mode: 'onChange',
    context: validatorContext.context,
    resolver: yupResolver(validator),
  });
  const { errors, control, formState, setValue, trigger } = formMethods;

  useEffect(() => {
    if (scoresAndAddonProductsData) {
      setValue('scores', scoresAndAddonProductsData.scores, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('addonProducts', scoresAndAddonProductsData.addonProducts, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [scoresAndAddonProductsData, setValue]);

  const groupedAddonProducts: Record<string, AddonProductLookup[]> = {};
  const groupDescMap: Record<string, string> = {};
  lookupsManager.getAddonProductsLookups().forEach((addonProductLookup) => {
    if (!groupDescMap[addonProductLookup.group]) {
      groupDescMap[addonProductLookup.group] =
        addonProductLookup.groupDescription || '';
    }
    groupedAddonProducts[addonProductLookup.group] = [
      ...(groupedAddonProducts[addonProductLookup.group] || []),
      addonProductLookup,
    ];
  });

  useEffect(() => {
    setAddonProductsTreeData(
      Object.keys(groupedAddonProducts)
        .map((group) => {
          return {
            title: (
              <Tooltip label={groupDescMap[group] || ''} placement='right'>
                {group}
              </Tooltip>
            ),
            key: group,
            checkable: false,
            children: (groupedAddonProducts[group] || []).map((product) => {
              return {
                title: (
                  <Tooltip label={product.description} placement='right'>
                    {product.name}
                  </Tooltip>
                ),
                key: product.code,
                checkable: !product.isDisabled,
              };
            }),
          };
        })
        .filter((a) => a.children.length > 0),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Flex direction='column' w='full'>
      <ProgramWizardStepDataProcessor
        stepKey={stepKey}
        trigger={trigger}
        triggerFieldNames={['scores', 'addonProducts']}
        validator={validator as ObjectSchema}
        model={scoresAndAddonProductsData}
        isDirty={formState.isDirty}
        validatorContext={validatorContext}
      />
      {isStepActive && (
        <form>
          <FormLayout header={'Scores and models'}>
            <FormControl isInvalid={!!errors.scores}>
              <Grid templateColumns='repeat(5,1fr)'>
                <GridItem justifyContent='end' display='flex'>
                  <FormLabel fontSize='md' htmlFor='scores' marginTop={1}>
                    Scores :
                  </FormLabel>
                </GridItem>
                <GridItem colSpan={3}>
                  <Controller
                    name='scores'
                    control={control}
                    defaultValue={scores}
                    render={({ value, onChange }) => (
                      <Select
                        isMulti
                        onChange={(values) => {
                          onChange(values);
                          const newModel = {
                            ...scoresAndAddonProductsData,
                            scores: values as string[],
                          };
                          setScoresAndAddonProductsData(newModel);
                        }}
                        selectContainerProps={{ width: '35rem' }}
                        showClearIcon={false}
                        showExpandIcon={false}
                        defaultValues={value}
                        isInvalid={!!errors.scores}
                        options={lookupsManager
                          .getScoresLookups()
                          .map((score) => ({
                            label: score.name,
                            value: score.code,
                          }))}
                      />
                    )}
                  />
                  <FormErrorMessage>
                    {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (errors.scores as any)?.message
                    }
                  </FormErrorMessage>
                  <FormHelperText fontSize='xs'>
                    Select up to 10 scores and models to append
                  </FormHelperText>
                </GridItem>
              </Grid>
            </FormControl>
          </FormLayout>
          <FormLayout header={'Add-On Products'}>
            <FormControl isInvalid={!!errors.addonProducts}>
              <Grid templateColumns='repeat(7, 1fr)'>
                <GridItem></GridItem>
                <GridItem colSpan={6}>
                  <Controller
                    name='addonProducts'
                    control={control}
                    defaultValue={addonProducts}
                    render={({ value, onChange }) => (
                      <>
                        {addonProductsTreeData.length > 0 ? (
                          <Tree
                            checkable
                            checkedKeys={value}
                            treeData={addonProductsTreeData}
                            onCheck={(addonProductsSelected) => {
                              onChange(addonProductsSelected);
                              const newModel = {
                                ...scoresAndAddonProductsData,
                                addonProducts:
                                  addonProductsSelected as string[],
                              };
                              setScoresAndAddonProductsData(newModel);
                            }}
                            defaultExpandedKeys={
                              addonProductsTreeData.length == 1
                                ? [addonProductsTreeData[0].key]
                                : []
                            }
                            selectable={false}
                            height={500}
                          />
                        ) : (
                          'There are no Add-On products available for selection.'
                        )}
                      </>
                    )}
                  />
                </GridItem>
              </Grid>
              <FormErrorMessage>
                {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (errors.addonProducts as any)?.message
                }
              </FormErrorMessage>
            </FormControl>
          </FormLayout>
        </form>
      )}
    </Flex>
  );
};
